import { TemplateSection, Template } from '../_types';

export type SectionInheritanceType = 'same-as-parent' | 'modified-from-parent' | 'new-in-child';

/**
 * Determines the inheritance type of a section by comparing effective (resolved) values
 * According to the inheritance model:
 * - Effective values represent what the user actually sees (after parent locks/unlocks are applied)
 * - Comparing effective values ensures badges reflect the visible state, not underlying raw data
 *
 * @param section - The child template section to check
 * @param parentTemplate - The parent template (from template.parentTemplate)
 * @returns The inheritance type, or null if this is not a child template section
 */
export const getSectionInheritanceType = (
  section: TemplateSection,
  parentTemplate?: Template | null,
): SectionInheritanceType | null => {
  // If there's no parentTemplateSectionId, this section is new in the child template
  if (section.parentTemplateSectionId === null) {
    return 'new-in-child';
  }

  // If no parent template is provided, we can't determine inheritance
  if (!parentTemplate) {
    return null;
  }

  // Find the parent section in the parent template using parentTemplateSectionId
  const parentSection = parentTemplate.templateSections.find((s) => s.id === section.parentTemplateSectionId);

  if (!parentSection) {
    return null;
  }

  // Check if the section has been deleted (soft delete)
  if (section.isDeleted) {
    return 'modified-from-parent'; // Deleted sections are modifications
  }

  // Compare effective (resolved) values with parent's effective values
  // This ensures badges reflect what the user sees, accounting for parent locks/unlocks
  const nameMatches = section.effectiveName === parentSection.effectiveName;
  const descriptionMatches = (section.effectiveDescription || '') === (parentSection.effectiveDescription || '');
  const requiredMatches = section.effectiveIsRequired === parentSection.effectiveIsRequired;
  const lockedMatches = section.effectiveIsLocked === parentSection.effectiveIsLocked;
  const repeatableMatches = section.effectiveIsRepeatable === parentSection.effectiveIsRepeatable;

  // If all effective properties match the parent's effective properties, it's same as parent
  if (nameMatches && descriptionMatches && requiredMatches && lockedMatches && repeatableMatches) {
    return 'same-as-parent';
  }

  // Otherwise, it's been modified from parent
  return 'modified-from-parent';
};

/**
 * Determines if a section is locked by its parent template
 *
 * A section is considered locked by parent when:
 * 1. The section's effectiveIsLocked is true (it's locked)
 * 2. The section's isLocked is null (meaning it inherits the lock from parent, not set by child)
 * 3. There is a parent template
 * 4. The section has a parent section (parentTemplateSectionId is not null)
 *
 * @param section - The template section to check
 * @param parentTemplate - The parent template (optional)
 * @returns true if the section is locked by the parent template, false otherwise
 */
export const isSectionLockedByParent = (section: TemplateSection, parentTemplate?: Template | null): boolean => {
  // Section is locked by parent if:
  // 1. effectiveIsLocked is true (it's locked)
  // 2. isLocked is null (not customized by child - inherited from parent)
  // 3. There is a parent template
  // 4. The section has a parent section ID
  return !!(
    section.effectiveIsLocked &&
    section.isLocked === null &&
    parentTemplate &&
    section.parentTemplateSectionId !== null
  );
};
